import React, { useEffect, useState } from 'react';

import { NavLink, useLocation } from 'react-router';

import {
  Activity,
  BarChart2,
  BarChart3,
  BookOpen,
  ChevronDown,
  ChevronLeft,
  CreditCard,
  Globe,
  Key,
  Layers,
  LayoutDashboard,
  Lightbulb,
  LogOut,
  Menu,
  Scan,
  Settings,
  ShieldAlert,
  SlidersHorizontal,
  Terminal,
  Users,
  Webhook,
  X,
} from 'lucide-react';

import { config } from '@/config';
import { useIsAdmin, useSignOut } from '@/features/auth';
import { useTheme } from '@/lib';
import { cn } from '@/lib/utils';
import { ROUTES } from '@/routes';

import styles from './Sidebar.module.scss';

// ── Nav items ─────────────────────────────────────────────────────────────────

type InternalNavItem = { to: string; icon: React.ElementType; label: string; adminOnly?: boolean };
type ExternalNavItem = { href: string; icon: React.ElementType; label: string };
type NavGroup = {
  group: string;
  icon: React.ElementType;
  children: InternalNavItem[];
  adminOnly?: boolean;
};
type NavItem = InternalNavItem | ExternalNavItem | NavGroup;

const NAV_ITEMS: NavItem[] = [
  { to: ROUTES.dashboard, icon: LayoutDashboard, label: 'Dashboard' },
  { to: ROUTES.apiKeys, icon: Key, label: 'API Keys' },
  { to: ROUTES.analytics, icon: BarChart3, label: 'Analytics' },
  {
    group: 'Webhooks',
    icon: Webhook,
    children: [
      { to: ROUTES.webhooks, icon: Webhook, label: 'Webhooks' },
      { to: ROUTES.webhookLogs, icon: Webhook, label: 'Logs' },
    ],
  },
  { to: ROUTES.activity, icon: Activity, label: 'Activity' },
  { to: ROUTES.mcpSessions, icon: Terminal, label: 'MCP Sessions' },
  { to: ROUTES.allowedHosts, icon: Globe, label: 'Allowed Hosts' },
  { to: ROUTES.modelCards, icon: Layers, label: 'Model Cards' },
  { to: ROUTES.howItWorks, icon: Lightbulb, label: 'How It Works' },
  { href: config.docsUrl, icon: BookOpen, label: 'Documentation' },
  { to: ROUTES.billing, icon: CreditCard, label: 'Billing' },
  {
    group: 'Settings',
    icon: Settings,
    children: [
      { to: ROUTES.settings, icon: Settings, label: 'Security' },
      { to: ROUTES.settingsOrganization, icon: Settings, label: 'Organization', adminOnly: true },
    ],
  },
  {
    group: 'Admin',
    icon: ShieldAlert,
    adminOnly: true,
    children: [
      { to: ROUTES.adminUsers, icon: Users, label: 'Users' },
      { to: ROUTES.adminApiUsage, icon: BarChart2, label: 'API Usage' },
      { to: ROUTES.adminDetections, icon: Scan, label: 'Detections' },
      { to: ROUTES.adminApiKeys, icon: Key, label: 'API Keys' },
      { to: ROUTES.adminAllowedHosts, icon: Globe, label: 'Allowed Hosts' },
      { to: ROUTES.adminThresholds, icon: SlidersHorizontal, label: 'Thresholds' },
    ],
  },
];

// Items shown in the mobile bottom tab bar
const BOTTOM_NAV_ITEMS = [
  { to: ROUTES.dashboard, icon: LayoutDashboard, label: 'Dashboard' },
  { to: ROUTES.apiKeys, icon: Key, label: 'API Keys' },
  { to: ROUTES.analytics, icon: BarChart3, label: 'Analytics' },
  { to: ROUTES.billing, icon: CreditCard, label: 'Billing' },
] as const;

const STORAGE_KEY = 'sidebar-collapsed';

// ── Component ─────────────────────────────────────────────────────────────────

export function Sidebar() {
  const theme = useTheme();
  const { pathname } = useLocation();

  const isAdmin = useIsAdmin();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(STORAGE_KEY) === 'true');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    for (const item of NAV_ITEMS) {
      if ('group' in item && item.children.some((c) => pathname.startsWith(c.to))) {
        initial.add(item.group);
      }
    }
    return initial;
  });
  const signOut = useSignOut();

  const toggleGroup = (group: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) {
        next.delete(group);
      } else {
        next.add(group);
      }
      return next;
    });
  };

  // Persist collapsed state
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(collapsed));
  }, [collapsed]);

  // Ctrl+B — toggle collapsed; Escape — close mobile drawer
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.ctrlKey && e.key === 'b') {
        e.preventDefault();
        setCollapsed((v) => !v);
      }
      if (e.key === 'Escape') setMobileOpen(false);
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  function closeMobile() {
    setMobileOpen(false);
  }

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && <div className={styles.backdrop} onClick={closeMobile} aria-hidden="true" />}

      {/* Sidebar — desktop sticky + mobile drawer */}
      <aside
        className={cn(
          styles.sidebar,
          collapsed ? styles.collapsed : styles.expanded,
          mobileOpen && styles.mobileOpen,
        )}
      >
        {/* Network bg behind the glass */}
        <div
          className="pointer-events-none absolute inset-0 bg-cover bg-center opacity-[0.50]"
          style={{
            backgroundImage:
              theme.resolvedTheme === 'dark'
                ? "url('/network-bg-mobile.png')"
                : "url('/network-bg-mobile-dark.png')",
          }}
          aria-hidden="true"
        />

        {/* Logo + mobile close */}
        <div className={styles.sidebarHeader}>
          <img
            src={
              collapsed
                ? '/moveris-logo-circle.png'
                : theme.resolvedTheme === 'dark'
                  ? '/moveris-logo-dark.svg'
                  : '/moveris-logo-light.svg'
            }
            alt="Moveris"
            className={cn(styles.sidebarLogo, collapsed && styles.sidebarLogoCollapsed)}
          />
          <button
            type="button"
            className={styles.mobileCloseBtn}
            onClick={closeMobile}
            aria-label="Close navigation"
          >
            <X size={18} />
          </button>
        </div>

        {/* Nav */}
        <nav className={styles.nav} aria-label="Main navigation">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;

            if ('group' in item) {
              if (item.adminOnly && !isAdmin) return null;
              const visibleChildren = item.children.filter((c) => !c.adminOnly || isAdmin);
              const isGroupExpanded = expandedGroups.has(item.group);
              const isGroupActive = visibleChildren.some((c) => pathname.startsWith(c.to));
              return (
                <div key={item.group} className={styles.navGroup}>
                  <button
                    type="button"
                    className={cn(
                      styles.navItem,
                      isGroupActive && !isGroupExpanded && styles.active,
                    )}
                    onClick={() => toggleGroup(item.group)}
                    aria-expanded={isGroupExpanded}
                  >
                    <Icon className={styles.navIcon} aria-hidden="true" />
                    <span className={styles.navLabel}>{item.group}</span>
                    <ChevronDown
                      className={cn(
                        styles.navGroupChevron,
                        isGroupExpanded && styles.navGroupChevronOpen,
                      )}
                      aria-hidden="true"
                    />
                  </button>
                  {isGroupExpanded && !collapsed && (
                    <div className={styles.navGroupChildren}>
                      {visibleChildren.map((child) => (
                        <NavLink
                          key={child.to}
                          to={child.to}
                          end
                          onClick={closeMobile}
                          className={({ isActive }) =>
                            cn(styles.navItem, styles.navGroupChild, isActive && styles.active)
                          }
                        >
                          <span className={styles.navGroupChildDot} aria-hidden="true" />
                          <span className={styles.navLabel}>{child.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            if ('href' in item) {
              return (
                <a
                  key={item.href}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.navItem}
                  onClick={closeMobile}
                >
                  <Icon className={styles.navIcon} aria-hidden="true" />
                  <span className={styles.navLabel}>{item.label}</span>
                </a>
              );
            }
            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={closeMobile}
                className={({ isActive }) => cn(styles.navItem, isActive && styles.active)}
              >
                <Icon className={styles.navIcon} aria-hidden="true" />
                <span className={styles.navLabel}>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        {/* Footer */}
        <div className={styles.sidebarFooter}>
          <button type="button" className={styles.navItem} onClick={signOut}>
            <LogOut className={styles.navIcon} aria-hidden="true" />
            <span className={styles.navLabel}>Sign out</span>
          </button>
          <button
            type="button"
            className={styles.collapseBtn}
            onClick={() => setCollapsed((v) => !v)}
            title="Toggle sidebar (Ctrl+B)"
          >
            <ChevronLeft className={cn(styles.navIcon, styles.collapseIcon)} />
            <span className={styles.collapseBtnLabel}>Collapse</span>
          </button>
        </div>
      </aside>

      {/* Mobile bottom tab bar */}
      <nav className={styles.bottomNav} aria-label="Mobile navigation">
        {BOTTOM_NAV_ITEMS.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) => cn(styles.bottomNavItem, isActive && styles.active)}
          >
            <Icon size={20} aria-hidden="true" />
            <span>{label}</span>
          </NavLink>
        ))}
        <button
          type="button"
          className={styles.bottomNavItem}
          onClick={() => setMobileOpen(true)}
          aria-label="Open navigation"
        >
          <Menu size={20} aria-hidden="true" />
          <span>More</span>
        </button>
      </nav>
    </>
  );
}
